import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface UseOverflowedElementsParams {
  elementsIds: string[];
  containerWidth: number;
  direction?: 'left' | 'right';
  maxLength?: number;
}

/**
 * Хук для управления отображением элементов с учетом переполнения контейнера
 *
 * @param {string[]} elementsIds Массив идентификаторов элементов
 * @param {number} containerWidth Ширина контейнера в пикселях
 * @param {'left' | 'right'} [direction='right'] Направление размещения элементов
 * @param {number} [maxLength] Максимальное количество отображаемых элементов
 *
 * @returns {Object} Объект с результатами
 * @returns {string[]} visibleButtons ID видимых элементов
 * @returns {string[]} hiddenButtons ID скрытых элементов
 * @returns {React.RefObject} elementsRefs Рефы на DOM-элементы
 */

export const useOverflowedElements = ({
  elementsIds,
  containerWidth,
  direction = 'right',
  maxLength,
}: UseOverflowedElementsParams) => {
  const elementsRefs = useRef<Record<string, HTMLElement | null>>({});
  const [elementWidths, setElementWidths] = useState<Record<string, number>>(
    {},
  );
  const [visibleElements, setVisibleElements] = useState<string[]>(elementsIds);
  const [hiddenElements, setHiddenElements] = useState<string[]>([]);

  useLayoutEffect(() => {
    if (!elementsIds.length) return;

    const allRefsExist = elementsIds.every((id) => elementsRefs.current[id]);
    if (!allRefsExist) return;

    const widths: typeof elementWidths = {};
    elementsIds.forEach((id) => {
      const elementRef = elementsRefs.current[id];
      if (elementRef) widths[id] = elementRef.offsetWidth;
    });
    setElementWidths(widths);
  }, [elementsIds]);

  useEffect(() => {
    const checkOverflow = () => {
      const availableWidth = containerWidth;

      let fitWidth = 0;
      const reversedButtons =
        direction === 'left' ? [...elementsIds].reverse() : elementsIds;
      const fittingButtons: string[] = [];

      reversedButtons.forEach((id, index) => {
        const width = elementWidths[id] || 0;
        if (
          fitWidth + width <= availableWidth &&
          (!maxLength || maxLength > index)
        ) {
          fittingButtons.unshift(id);
          fitWidth += width;
        } else {
          return;
        }
      });

      const hidden = elementsIds.filter((id) => !fittingButtons.includes(id));
      setVisibleElements(fittingButtons);
      setHiddenElements(hidden);
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [elementWidths, containerWidth, elementsIds, direction, maxLength]);

  return { visibleElements, hiddenElements, elementsRefs };
};
